// Idle-Logout-Hook: Loggt automatisch aus, wenn der Tab idle ist.
//
// DSGVO-Hinweis: Sensible Patientendaten dürfen nicht auf einem unbeaufsichtigten
// Bildschirm sichtbar bleiben. Wir setzen ein clientseitiges Idle-Timeout (20 Min),
// das durch Mausbewegung/Tasten/Klick neu gestartet wird. Server hat zusätzlich ein
// hartes Idle-Timeout (30 Min) als Backstop.

import { useEffect, useRef } from 'react';

const IDLE_TIMEOUT_MS = 20 * 60 * 1000; // 20 Minuten
const WARNING_MS = 60 * 1000;            // 1 Minute Warnung vor Logout

interface UseIdleLogoutOpts {
  enabled: boolean;
  onIdle: () => void;
  onWarning?: () => void;
}

export function useIdleLogout({ enabled, onIdle, onWarning }: UseIdleLogoutOpts) {
  const timer = useRef<number | null>(null);
  const warning = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const reset = () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
      if (warning.current !== null) window.clearTimeout(warning.current);
      warning.current = window.setTimeout(() => onWarning?.(), IDLE_TIMEOUT_MS - WARNING_MS);
      timer.current = window.setTimeout(() => onIdle(), IDLE_TIMEOUT_MS);
    };

    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'click'] as const;
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();

    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (timer.current !== null) window.clearTimeout(timer.current);
      if (warning.current !== null) window.clearTimeout(warning.current);
    };
  }, [enabled, onIdle, onWarning]);
}
